import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { isEmpty, first } from "lodash";

// SHARED
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";

//HELPERS AND SERVICES
import { fetchDropOff } from "Services/delivery.services";
import { fetchServicePoint } from "Services/delivery-settings.services";
import { fetchCustomer } from "Services/customer.service";
import { decode } from "Helpers/enc";
// SCSS
import "../parcel-tracking-view.scss";

// HOOKS
import useSession from "Hooks/session.hook";

// COMPONENTS
import ParcelTrackingDropoffViewComponent from "Components/delivery-management/parcel-tracking-view/dropoff-view-component";
import { updateOrderDeliveryDate } from "Services/order.service";

const useParcelTrackingDropOffViewPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [parcelDetails, setParcelDetails] = useState<any>({});
  const [customerName, setCustomerName] = useState<string>("");
  const [dropoffLocationName, setDropoffLocationName] = useState<string>("");

  const handleFetchParcelDetails = async (pickupID: any) => {
    const responseData = await fetchDropOff(pickupID);
    if (responseData && !responseData.error) {
      setParcelDetails(responseData);
    }
  };

  const handleFetchCustomerAndDropOffDetails = async () => {
    if (!parcelDetails.merchantID) return;
    if (!parcelDetails.dropOffLocation) return;

    const mergedResponseData = await Promise.all([
      fetchCustomer(parcelDetails?.merchantID),
      fetchServicePoint(parcelDetails?.dropOffLocation),
    ]);

    // if there is an error in the first element, prevent everything from being inserted
    const hasError = first(mergedResponseData)?.error;

    if (!hasError) {
      setCustomerName(mergedResponseData[0]?.customerName);
      setDropoffLocationName(mergedResponseData[1]?.locationName);
    }
  };

  const handleUpdateDeliveryDate = async (data) => {
    setIsLoading(true);

    const responseData = await updateOrderDeliveryDate(data);

    if (!responseData.error) {
      setIsLoading(false);
      setParcelDetails((prev) => ({
        ...prev,
        deliveryDate: data.deliveryDate,
      }));
      return true;
    } else {
      setIsLoading(false);
      return false;
    }
  };

  return {
    isLoading,
    setIsLoading,
    parcelDetails,
    setParcelDetails,
    customerName,
    setCustomerName,
    dropoffLocationName,
    setDropoffLocationName,
    handleUpdateDeliveryDate,
    handleFetchParcelDetails,
    handleFetchCustomerAndDropOffDetails,
  };
};

const ParcelTrackingDropoffViewPage = ({}) => {
  const { t } = useTranslation();
  const { org, roleTypes } = useSession();
  const { id } = useParams<{ id: string }>();

  const {
    isLoading,
    setIsLoading,
    parcelDetails,
    setParcelDetails,
    customerName,
    setCustomerName,
    dropoffLocationName,
    setDropoffLocationName,
    handleUpdateDeliveryDate,
    handleFetchParcelDetails,
    handleFetchCustomerAndDropOffDetails,
  } = useParcelTrackingDropOffViewPage();

  // const [parcelState, setParcelState] = useRecoilState<any>(parcelAtom)
  const decodedOrderID = decode(id);

  useEffect(() => {
    handleFetchParcelDetails(decodedOrderID);
  }, []);

  useEffect(() => {
    handleFetchCustomerAndDropOffDetails();
  }, [parcelDetails]);

  return (
    <div>
      <Helmet>
        <title>{t("title.deliveryManagement")}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.deliveryManagement")}
        haveSubtitle
        subtitleText={t("title.dropOffTask")}
      />
      <ParcelTrackingDropoffViewComponent
        parcelDetails={parcelDetails}
        customerName={customerName}
        dropoffLocationName={dropoffLocationName}
        onUpdateDeliveryDate={handleUpdateDeliveryDate}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ParcelTrackingDropoffViewPage;
